import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useHref, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SideSheet } from '../components/ui/SideSheet';
import { buttonClassName } from '../components/ui/buttonClassName';
import { ErrorState, LoadingState } from '../components/ui/AsyncState';
import { HELP_CONTEXTS } from './contexts';
import { HelpApiContext, type HelpApi, type HelpLayer } from './helpApi';
import { HelpArticle, type ZoomTarget } from './HelpArticle';
import { ImageZoom } from './ImageZoom';
import { sectionsForPath } from './match';
import { loadHelpContent, useHelpContent } from './useHelpContent';
import styles from './Help.module.css';

/** Typing "?" in a field is text, not a shortcut. */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable;
}

interface Props {
  /** The current page's title ("Payments"), for "Help: Payments". */
  pageTitle: string;
  children: ReactNode;
}

/**
 * Contextual help for the console: the page header's "?" (and a dialog's), or the "?" key anywhere
 * outside a field, opens a side sheet (bottom sheet on phones) with the guide sections for what is on
 * screen — the top open dialog's context, else the current route — and "All help topics" (the /help page; there is no nav entry — help is reached from the "?").
 */
export function HelpProvider({ pageTitle, children }: Props) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState<{ layer: HelpLayer | null } | null>(null);
  const [zoom, setZoom] = useState<ZoomTarget | null>(null);
  const layers = useRef<HelpLayer[]>([]);
  // Demo builds route on the hash ('#/'); window.open needs the full href.
  const rootHref = useHref('/').replace(/\/$/, '');

  const openHelp = useCallback((layer?: HelpLayer) => {
    setOpen({ layer: layer ?? layers.current[layers.current.length - 1] ?? null });
  }, []);
  const closeHelp = useCallback(() => setOpen(null), []);
  const pushLayer = useCallback((layer: HelpLayer) => {
    layers.current.push(layer);
    return () => {
      layers.current = layers.current.filter((l) => l !== layer);
    };
  }, []);
  const api = useMemo<HelpApi>(() => ({ openHelp, closeHelp, pushLayer }), [openHelp, closeHelp, pushLayer]);

  // Leaving the page closes its help.
  useEffect(() => setOpen(null), [pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      if (pathname.startsWith('/help')) return;
      e.preventDefault();
      openHelp();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openHelp, pathname]);

  // Warm the help chunk in the background once the console has settled.
  useEffect(() => {
    const id = window.setTimeout(() => void loadHelpContent().catch(() => undefined), 4000);
    return () => window.clearTimeout(id);
  }, []);

  const layer = open?.layer ?? null;
  const title = t('help.sheetTitle', { page: layer?.title ?? pageTitle });
  return (
    <HelpApiContext.Provider value={api}>
      {children}
      <SideSheet open={!!open} title={title} onClose={closeHelp} footer={open ? <SheetFooter layer={layer} pathname={pathname} /> : null}>
        {open && (
          <SheetBody
            layer={layer}
            pathname={pathname}
            onZoom={setZoom}
            onNavigate={(to) => {
              if (layer) {
                // A dialog is open: keep what was typed, open the guide in a new tab.
                window.open(`${rootHref}${to}`, '_blank', 'noopener');
                return;
              }
              closeHelp();
              navigate(to);
            }}
          />
        )}
      </SideSheet>
      <ImageZoom target={zoom} onClose={() => setZoom(null)} />
    </HelpApiContext.Provider>
  );
}

function useSheetSections(layer: HelpLayer | null, pathname: string) {
  const state = useHelpContent(true);
  const ctx = layer ? HELP_CONTEXTS[layer.context] : null;
  const sections = state.status === 'ready' ? sectionsForPath(state.content, ctx?.route ?? pathname, ctx?.topic) : [];
  return { state, sections };
}

function SheetBody({
  layer,
  pathname,
  onZoom,
  onNavigate,
}: {
  layer: HelpLayer | null;
  pathname: string;
  onZoom: (z: ZoomTarget) => void;
  onNavigate: (to: string) => void;
}) {
  const { t } = useTranslation();
  const { state, sections } = useSheetSections(layer, pathname);
  if (state.status === 'loading') return <LoadingState label={t('help.loading')} />;
  if (state.status === 'error') return <ErrorState title={t('help.loadError')} onRetry={state.retry} />;
  if (!sections.length) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyTitle}>{t('help.noSectionTitle')}</p>
        <p className={styles.emptyBody}>{t('help.noSectionBody')}</p>
      </div>
    );
  }
  return (
    <div className={styles.sheetSections}>
      {sections.map((s) => (
        <HelpArticle key={s.id} section={s} level={3} onNavigate={onNavigate} onZoom={onZoom} id={`sheet-${s.id}`} />
      ))}
    </div>
  );
}

function SheetFooter({ layer, pathname }: { layer: HelpLayer | null; pathname: string }) {
  const { t } = useTranslation();
  const { sections } = useSheetSections(layer, pathname);
  const to = sections[0] ? `/help/${sections[0].id}` : '/help';
  const href = useHref(to);
  if (layer) {
    return (
      <a className={`${buttonClassName('primary')} ${styles.fullGuide}`} href={href} target="_blank" rel="noopener">
        {t('help.openFullGuideNewTab')}
      </a>
    );
  }
  return (
    <Link className={`${buttonClassName('primary')} ${styles.fullGuide}`} to={to}>
      {t('help.openFullGuide')}
    </Link>
  );
}
